import React from 'react';
import PropTypes from 'prop-types';

// DualRangeSlider: custom dual-thumb slider using divs and mouse/touch events
function DualRangeSlider({ min, max, value, onChange }) {
  const [dragging, setDragging] = React.useState(null); // 'min' | 'max' | null
  const sliderRef = React.useRef();

  // Calculate percent for a value
  const getPercent = v => ((v - min) / (max - min)) * 100;

  // Convert pixel X to value
  const pxToValue = px => {
    const rect = sliderRef.current.getBoundingClientRect();
    const percent = Math.min(1, Math.max(0, (px - rect.left) / rect.width));
    return Math.round(percent * (max - min) + min);
  };

  // Mouse/touch handlers
  const startDrag = which => e => {
    e.preventDefault();
    setDragging(which);
  };
  
  React.useEffect(() => {
    if (!dragging) return;
    const move = e => {
      let clientX = e.touches ? e.touches[0].clientX : e.clientX;
      let newVal = pxToValue(clientX);
      let [minVal, maxVal] = value;
      if (dragging === 'min') {
        newVal = Math.min(newVal, maxVal - 1);
        newVal = Math.max(min, newVal);
        if (newVal !== minVal) onChange([newVal, maxVal]);
      } else {
        newVal = Math.max(newVal, minVal + 1);
        newVal = Math.min(max, newVal);
        if (newVal !== maxVal) onChange([minVal, newVal]);
      }
    };
    const up = () => setDragging(null);
    window.addEventListener('mousemove', move);
    window.addEventListener('touchmove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchend', up);
    };
  }, [dragging, value, min, max, onChange]);

  // Keyboard support
  const handleThumbKeyDown = (which, idx) => e => {
    let [minVal, maxVal] = value;
    if (which === 'min') {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
        const newVal = Math.max(min, minVal - 1);
        if (newVal < maxVal) onChange([newVal, maxVal]);
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
        const newVal = Math.min(maxVal - 1, minVal + 1);
        onChange([newVal, maxVal]);
      }
    } else {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
        const newVal = Math.max(minVal + 1, maxVal - 1);
        onChange([minVal, newVal]);
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
        const newVal = Math.min(max, maxVal + 1);
        if (newVal > minVal) onChange([minVal, newVal]);
      }
    }
  };

  const [minVal, maxVal] = value;

  return (
    <div ref={sliderRef} style={{ position: 'relative', width: '100%', height: 56, margin: '0 8px', display: 'flex', alignItems: 'center', touchAction: 'none' }}>
      {/* Track background */}
      <div style={{
        position: 'absolute',
        top: 28,
        left: 0,
        right: 0,
        height: 8,
        background: '#e3e8ef',
        borderRadius: 4,
        zIndex: 0
      }} />
      {/* Track highlight */}
      <div style={{
        position: 'absolute',
        top: 28,
        left: `${getPercent(minVal)}%`,
        right: `${100 - getPercent(maxVal)}%`,
        height: 8,
        background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)',
        borderRadius: 4,
        zIndex: 1,
        boxShadow: '0 2px 8px #1976d222'
      }} />
      {/* Min thumb */}
      <div
        role="slider"
        tabIndex={0}
        aria-valuemin={min}
        aria-valuemax={maxVal - 1}
        aria-valuenow={minVal}
        aria-label="Başlangıç yılı"
        onMouseDown={startDrag('min')}
        onTouchStart={startDrag('min')}
        onKeyDown={handleThumbKeyDown('min', 0)}
        style={{
          position: 'absolute',
          top: 16,
          left: `calc(${getPercent(minVal)}% - 12px)`,
          width: 24,
          height: 24,
          borderRadius: '50%',
          background: dragging === 'min' ? '#fff' : '#fff',
          border: `3px solid ${dragging === 'min' ? '#42a5f5' : '#1976d2'}`,
          boxShadow: '0 2px 8px #1976d244',
          cursor: 'pointer',
          zIndex: 3,
          transition: 'border 0.2s, box-shadow 0.2s',
          outline: dragging === 'min' ? '2px solid #42a5f5' : 'none',
        }}
      />
      {/* Max thumb */}
      <div
        role="slider"
        tabIndex={0}
        aria-valuemin={minVal + 1}
        aria-valuemax={max}
        aria-valuenow={maxVal}
        aria-label="Bitiş yılı"
        onMouseDown={startDrag('max')}
        onTouchStart={startDrag('max')}
        onKeyDown={handleThumbKeyDown('max', 1)}
        style={{
          position: 'absolute',
          top: 16,
          left: `calc(${getPercent(maxVal)}% - 12px)`,
          width: 24,
          height: 24,
          borderRadius: '50%',
          background: dragging === 'max' ? '#fff' : '#fff',
          border: `3px solid ${dragging === 'max' ? '#42a5f5' : '#1976d2'}`,
          boxShadow: '0 2px 8px #1976d244',
          cursor: 'pointer',
          zIndex: 4,
          transition: 'border 0.2s, box-shadow 0.2s',
          outline: dragging === 'max' ? '2px solid #42a5f5' : 'none',
        }}
      />
    </div>
  );
}

DualRangeSlider.propTypes = {
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  value: PropTypes.arrayOf(PropTypes.number).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default DualRangeSlider;
